/**
 * TokenBalanceContext
 *
 * Centralized context for token balance state.
 * Fetches balance ONCE and shares it across all consumers,
 * eliminating duplicate API calls from multiple useTokenBalance() instances.
 */

import { createContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { useAuth } from '../hooks/useAuth';
import { buildCloudApiUrl } from '../services/urlResolver';
import { isServerlessMode, isElectronMode } from '../utils/apiMode';
import { extractErrorMessage, handleResponse } from '../utils/httpUtils';

/**
 * Full balance information returned from the API.
 */
export interface TokenBalanceInfo {
  balance: number;
  expirable_balance: number;
  permanent_balance: number;
  total_purchased: number;
  total_earned: number;
  total_spent: number;
  first_login_bonus_claimed: boolean;
  last_monthly_credit: string | null;
  subscription_tier: string;
  can_claim_monthly: boolean;
  first_login_bonus_granted: boolean;
  monthly_tokens_granted: boolean;
  next_expiration: string | null;
  expiring_soon_amount: number;
}

export interface TokenBalanceContextType {
  /**
   * Total balance (expirable + permanent), or null when it is NOT KNOWN: not
   * signed in, not fetched yet, or the server could not be read. Never 0 for
   * "unknown" -- an unreachable server used to read as an empty account
   * ("0 tokens", "Insufficient Tokens") to a customer whose money was intact.
   */
  balance: number | null;
  /** Full balance breakdown */
  fullBalance: TokenBalanceInfo | null;
  /** Loading state */
  loading: boolean;
  /** Error message if any */
  error: string | null;
  /** Refresh balance from server */
  refresh: () => Promise<void>;
}

// eslint-disable-next-line react-refresh/only-export-components
export const TokenBalanceContext = createContext<TokenBalanceContextType | undefined>(undefined);

interface TokenBalanceProviderProps {
  children: ReactNode;
}

export function TokenBalanceProvider({ children }: TokenBalanceProviderProps) {
  const { user, getIdToken } = useAuth();
  const [balance, setBalance] = useState<number | null>(null);
  const [fullBalance, setFullBalance] = useState<TokenBalanceInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchBalance = useCallback(async () => {
    if (!user || (!isServerlessMode() && !isElectronMode())) {
      setBalance(null);
      setFullBalance(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      console.log('[TokenBalance] Fetching balance for user:', user.uid);
      const token = await getIdToken();
      if (!token) {
        console.warn('[TokenBalance] No auth token available');
        setBalance(null);
        setFullBalance(null);
        setLoading(false);
        return;
      }

      const url = buildCloudApiUrl('/api/v1/tokens/balance');
      console.log('[TokenBalance] Requesting:', url);

      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      console.log('[TokenBalance] Response status:', response.status);

      // handleResponse parses only the API's own envelope; anything else (a
      // gateway's HTML error page, a network failure) throws, and the balance
      // becomes unknown rather than zero.
      const balanceInfo = await handleResponse<TokenBalanceInfo>(response);
      console.log('[TokenBalance] Balance received:', balanceInfo.balance);
      setBalance(balanceInfo.balance);
      setFullBalance(balanceInfo);
    } catch (err) {
      console.error('[TokenBalance] Fetch failed:', err);
      setError(extractErrorMessage(err, 'Failed to fetch balance'));
      setBalance(null);
      setFullBalance(null);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid]);

  // Fetch balance on mount and when user changes
  useEffect(() => {
    fetchBalance();
  }, [fetchBalance]);

  // Listen for global token-balance-changed events
  useEffect(() => {
    const handler = () => {
      fetchBalance();
    };
    window.addEventListener('token-balance-changed', handler);
    return () => window.removeEventListener('token-balance-changed', handler);
  }, [fetchBalance]);

  const value: TokenBalanceContextType = {
    balance,
    fullBalance,
    loading,
    error,
    refresh: fetchBalance,
  };

  return <TokenBalanceContext.Provider value={value}>{children}</TokenBalanceContext.Provider>;
}
